// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { canAddToCart } from "@eisinc/App/Store/Selectors/ProductSelectors";
import { getValidatedQty } from "@eisinc/App/Utilities/helpers";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { UnitOfMeasureData } from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import { addToCartStatus } from "@eisinc/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import { Clickable, Icon, LoadingSpinner } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { NamedExoticComponent, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps | ProductAddToCartLinkStyles;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    iconOnly?: boolean;
    alwaysVisible?: boolean;
    asLink?: boolean;
    withIcon?: boolean;
    iconColor?: string;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

// <XNG-Changes>
export interface IconConfig {
    src: NamedExoticComponent | string;
    position: "left" | "right";
    height: number;
    width: number;
}

// </XNG-Changes>

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    ButtonPresentationProps;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export interface ProductAddToCartLinkStyles {
    addToCartClickable?: ClickablePresentationProps;
    linkIconTextWrapper?: InjectableCss;
    linkIcon?: IconPresentationProps;
    linkText?: TypographyPresentationProps;
}

export const productAddToCartButtonStyles: ButtonPresentationProps = {};
export const productAddToCartIconButtonStyles: ButtonPresentationProps = {};
export const productAddToCartLinkStyles: ProductAddToCartLinkStyles = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, properties, allowZeroPricing, quoteRequired, minimumOrderQty },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    iconOnly,
    alwaysVisible,
    asLink,
    withIcon,
    iconColor,
    subscription,
    onAddToCartClick,
    ...otherProps
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const toasterContext: any = React.useContext(ToasterContext);
    const [iconButtonStyles] = React.useState(() => mergeToNew(productAddToCartIconButtonStyles, extendedStyles));
    const [linkStyles] = React.useState(() => mergeToNew(productAddToCartLinkStyles, extendedStyles));

    const [status, setStatus] = React.useState(addToCartStatus.default);
    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(properties?.unitOfMeasureData || "[]");

    useEffect(() => {
        let timeout: any;
        if (status === addToCartStatus.success) {
            timeout = setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 3000);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [status]);
    if (!alwaysVisible && ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart)) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        setStatus(addToCartStatus.loading);
        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        let qtyTobeAdded = qtyOrdered;

        if (unitOfMeasuresData?.length > 0 && unitOfMeasure) {
            const selectedUOM = unitOfMeasuresData.find(uom => uom.UnitOfMeasure === unitOfMeasure);
            const minQty = selectedUOM
                ? parseFloat(selectedUOM.MinimumOrderQuantity) || 1
                : parseFloat(properties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString()) || 1;
            const msq = selectedUOM
                ? parseFloat(selectedUOM.MultipleSaleQuantity) || 1
                : parseFloat(properties?.multipleSaleQty) || 1;

            const validatedQty = getValidatedQty(qtyOrdered, minQty, msq);
            qtyTobeAdded = validatedQty;
        } else {
            const minQty = parseFloat(properties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString()) || 1;
            if (!qtyTobeAdded || qtyTobeAdded < minQty) {
                qtyTobeAdded = minQty;
            }
        }

        const cartLine = (await addToCart({
            productId,
            qtyOrdered: qtyTobeAdded,
            unitOfMeasure,
            notes,
            sectionOptions,
        })) as CartLineModel;

        setStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartLine.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    const isDisabled =
        isAddToCartButtonDisabled ||
        status !== addToCartStatus.default ||
        (alwaysVisible && ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart));

    const iconConfig: IconConfig = {
        src:
            status === addToCartStatus.default && iconOnly
                ? addToCartStatus.default.icon
                : status === addToCartStatus.success
                  ? Check
                  : "",
        position: "left",
        height: 14,
        width: 16,
    };

    if (iconOnly) {
        return (
            <Button
                {...iconButtonStyles}
                onClick={addToCartClickHandler}
                className={`button-wrapper-icon-only ${status.status}`}
                disabled={isDisabled}
                icon={iconConfig}
                {...otherProps}
                // xng-changed aria-label to fix the accessibility in case there was no data
                aria-label="add to cart icon button"
                {...{ isLoading: status === addToCartStatus.loading }}
            >
                {/* {status === addToCartStatus.loading ? (
                    <StyledWrapper className="center">
                        <StyledWrapper {...dotsStyle} />
                    </StyledWrapper>
                ) : (
                    <Icon src={Cart} size={20} color="inherit" />
                )} */}
            </Button>
        );
    }

    if (asLink) {
        return (
            <Clickable disabled={isDisabled} onClick={addToCartClickHandler} {...linkStyles.addToCartClickable}>
                {status === addToCartStatus.loading ? (
                    <LoadingSpinner size={22} color="primary" />
                ) : (
                    <StyledWrapper {...linkStyles.linkIconTextWrapper}>
                        {withIcon && <Icon src={status.icon} {...linkStyles.linkIcon} className="as-link-icon" />}
                        <Typography {...linkStyles.linkText} className="as-link-text">
                            {labelOverride ?? translate(status.label)}
                        </Typography>
                    </StyledWrapper>
                )}
            </Clickable>
        );
    }

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={isDisabled}
            {...otherProps}
            // XNG-Changes
            {...{ isLoading: status === addToCartStatus.loading }}
            icon={iconConfig}
        >
            <Typography as="span">{labelOverride ?? translate(status.label)}</Typography>
        </Button>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
